import { QrCode, Search, MessageCircle } from "lucide-react";
import { useState } from "react";
import { StickerPacksDrawer } from "@/components/stickers/StickerPacksDrawer";

interface AboutHowItWorksSectionProps {
  openDemoProfile?: (profile: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    x_username: string | null;
    bio: string | null;
    metro_card_number: string | null;
    created_at: string;
    updated_at: string;
    show_email: boolean;
    show_mobile: boolean;
    metefy_token: string | null;
    photo_url?: string | null;
    mask_bio?: boolean;
    social_sms?: string | null;
    social_call?: string | null;
    social_telegram?: string | null;
    social_whatsapp?: string | null;
    use_social_icons?: boolean;
  }) => void;
}

const steps = [
  {
    icon: QrCode,
    number: "01",
    title: "Attach QR Code",
    description: "Place Metefy's unique QR sticker on your valuable items like metro cards, laptops, or personal belongings."
  },
  {
    icon: Search,
    number: "02", 
    title: "Someone Finds It",
    description: "When someone finds your lost item, they simply scan the QR code with their smartphone camera."
  },
  {
    icon: MessageCircle,
    number: "03",
    title: "Instant Connection",
    description: "The finder is immediately connected to you through our secure platform, enabling quick and safe return."
  }
];

export function AboutHowItWorksSection({ openDemoProfile }: AboutHowItWorksSectionProps = {}) {
  const [isStickerDrawerOpen, setIsStickerDrawerOpen] = useState(false);
  
  // Generate HowTo Schema for SEO
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Use Metefy QR Technology for Item Recovery",
    "description": "Learn how to protect your belongings and enable quick recovery using Metefy's QR code technology in 3 simple steps.",
    "image": "https://metefy.com/og-image.png",
    "totalTime": "PT2M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": "179"
    },
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description,
      "url": "https://metefy.com/about#how-it-works"
    }))
  };

  return (
    <section id="how-it-works" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <QrCode className="w-4 h-4 text-foreground" />
            <span className="text-foreground">How It Works</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Simple <span className="text-primary">3-Step Process</span>
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
            Getting started with Metefy is incredibly simple. Follow these three easy steps to protect your belongings.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div key={index} className="text-center group">
              <div className="relative mb-8">
                <div className="w-20 h-20 bg-card border-2 border-primary/30 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:border-primary group-hover:scale-110 transition-all duration-300">
                  <step.icon className="w-8 h-8 text-primary group-hover:scale-110 transition-all duration-300" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-4">
                {step.title}
              </h3>
              <p className="text-foreground/80 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <button
            onClick={() => setIsStickerDrawerOpen(true)}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
          >
            Get QR Stickers
            <QrCode className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Inject HowTo Schema for SEO - hidden from users */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
        aria-hidden="true"
      ></script>
      
      <StickerPacksDrawer isOpen={isStickerDrawerOpen} setIsOpen={setIsStickerDrawerOpen} openDemoProfile={openDemoProfile} />
    </section>
  );
} 